import { Component } from 'react';

import { Tag } from '@zola/zola-ui/src/components/Tag';

import { CarouselProvider, Image, ImageWithZoom } from 'pure-react-carousel';
import LazyLoad from 'react-lazyload';
import Media from 'react-media';
import Slider from 'react-slick';

import { SCREEN_SIZE_XS } from './helpers';
import YouTubeModal from './YouTubeModal';

import '../../../node_modules/slick-carousel/slick/slick.css';
import './ImageCarousel.less';

const getProductImages = (activeProductLook, youtubeId, personalizedImageUrl) => {
  if (!activeProductLook) return [];
  const { images } = activeProductLook;
  const productImages = [];
  images.forEach((image) => {
    if (image.aspect_ratios['2x3']) {
      productImages.push(image.aspect_ratios['2x3']); // TODO: remove this once we cut everything over
    } else if (image.aspect_ratios['3x4']) {
      productImages.push(image.aspect_ratios['3x4']);
    } else {
      productImages.push(image.aspect_ratios['1x1']);
    }
  });
  if (youtubeId) {
    const youtubeImage = Object.assign({}, images[0].aspect_ratios['1x1']);
    youtubeImage.youtubeId = youtubeId;
    productImages.push(youtubeImage);
  }
  if (personalizedImageUrl) {
    productImages.splice(0, 0, {
      large: personalizedImageUrl,
      thumb: personalizedImageUrl,
    });
  }
  return productImages;
};

export default class ImageCarousel extends Component {
  constructor(props) {
    super();
    this.state = {
      activeThumb: 0,
      personalizedImageUrl: props.personalizedImageUrl,
      productImages: getProductImages(
        props.activeProductLook,
        props.productData.youtube_video_id,
        props.personalizedImageUrl
      ),
      productLookId: props.activeProductLook.id,
      videoId: props.productData.youtube_video_id,
      showVideo: false,
    };

    this.goTo = this.goTo.bind(this);
    this.showYoutubeModal = this.showYoutubeModal.bind(this);
    this.state.sliderSettings = {
      dots: true,
      infinite: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
    };
    this.state.thumbSettings = {
      draggable: false,
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: 4,
      slidesToScroll: 4,
      responsive: [
        {
          breakpoint: 1280,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4,
          },
        },
        {
          breakpoint: 991,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
      ],
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { activeProductLook, personalizedImageUrl } = nextProps;
    if (personalizedImageUrl !== prevState.personalizedImageUrl) {
      return {
        personalizedImageUrl,
        productImages: getProductImages(activeProductLook, prevState.videoId, personalizedImageUrl),
      };
    }
    if (activeProductLook.id !== prevState.productLookId) {
      return {
        activeThumb: 0,
        productImages: getProductImages(activeProductLook, prevState.videoId, personalizedImageUrl),
        productLookId: activeProductLook.id,
      };
    }
    return prevState;
  }

  goTo(event) {
    const index = Number(event.target.getAttribute('index'));
    const video = event.target.getAttribute('type') === 'video';
    this.setState({ activeThumb: index }, () => {
      this.slider.slickGoTo(index);
      if (video) this.showYoutubeModal();
    });
  }

  showYoutubeModal() {
    const { showVideo } = this.state;
    this.setState({ showVideo: !showVideo });
  }

  render() {
    const { activeThumb, sliderSettings, thumbSettings, productImages, videoId, showVideo } =
      this.state;
    const { activeProductLook, productData } = this.props;
    const { badge } = activeProductLook;
    const displayName = productData.display_name;
    const personalizationAvailable = productData && productData.personalizable;

    return (
      <div key={activeProductLook.id} className="carousel_container">
        {showVideo ? (
          <LazyLoad once>
            <YouTubeModal showYoutubeModal={this.showYoutubeModal} videoId={videoId} />
          </LazyLoad>
        ) : null}
        <Slider
          // eslint-disable-next-line
          ref={(c) => (this.slider = c)}
          className="main-slider"
          {...sliderSettings}
        >
          {productImages.map((image, i) => {
            if (image.youtubeId) {
              return (
                <div className="carousel-slide video-thumbnail" key={`carousel-slide-${i}`}>
                  <div className="zolaicon-container">
                    <div className="pdp-play-button" />
                  </div>
                  <img onClick={this.showYoutubeModal} alt={displayName} src={image.large} />
                </div>
              );
            }
            return (
              <div className="carousel-slide" key={`carousel-slide-${i}`}>
                {badge && i === 0 ? (
                  <img className="carousel-slide-badge" alt={badge.title} src={badge.image_url} />
                ) : null}
                {personalizationAvailable && i === 0 && (
                  <Tag
                    type="emoji"
                    size="lg"
                    className="personalize-banner"
                    emoji={{ ariaLabel: 'pencil emoji', symbol: '✏️' }}
                  >
                    Personalizable
                  </Tag>
                )}
                <CarouselProvider touchEnabled={false} className="carousel-with-image">
                  {/* TODO attempt to add support for rectangular images for ProductImageGallery */}
                  <Image className="carousel-image-anchor" src={image.large} />
                  <ImageWithZoom
                    className="image-with-zoom"
                    src={image.large}
                    srcZoomed={image.full}
                  />
                </CarouselProvider>
              </div>
            );
          })}
        </Slider>
        <Media query={`(min-width: ${SCREEN_SIZE_XS})`}>
          <div className="thumbnail_container">
            {productImages.length > 1 ? (
              <Slider className="thumbnail-slider" {...thumbSettings}>
                {productImages.map((image, i) => {
                  if (image.youtubeId) {
                    return (
                      <div
                        className="carousel-thumbnail"
                        key={`carousel-thumb-${i}`}
                        // eslint-disable-next-line react/no-unknown-property
                        index={i}
                        onClick={this.goTo}
                      >
                        <div className="zolaicon-container">
                          <div className="pdp-play-button" />
                        </div>
                        <img
                          alt={displayName}
                          className={activeThumb === i ? 'is-active' : ''}
                          type="video"
                          // eslint-disable-next-line react/no-unknown-property
                          index={i}
                          src={image.thumb}
                        />
                      </div>
                    );
                  }
                  return (
                    // eslint-disable-next-line react/no-unknown-property
                    <div key={`carousel-thumb-${i}`} index={i} onClick={this.goTo}>
                      <LazyLoad once>
                        <img
                          alt={displayName}
                          className={activeThumb === i ? 'is-active' : ''}
                          // eslint-disable-next-line react/no-unknown-property
                          index={i}
                          src={image.thumb}
                        />
                      </LazyLoad>
                    </div>
                  );
                })}
              </Slider>
            ) : null}
          </div>
        </Media>
      </div>
    );
  }
}
